import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LOCALES, Locale } from "@/i18n";

const SITE_URL = "https://lacontessina.com";
const DEFAULT_OG = "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/a585d782-89cf-404c-b70a-a34eb8576dbe";

interface Props {
  title: string;
  description?: string;
  image?: string;
  type?: "website" | "article" | "product";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * Per-route <head> management:
 *  - title (max ~60 chars), description (max ~160 chars)
 *  - canonical and hreflang for every supported locale (+ x-default)
 *  - OpenGraph + Twitter cards
 *  - Optional JSON-LD payload
 */
export default function SEO({ title, description, image = DEFAULT_OG, type = "website", jsonLd }: Props) {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const currentLocale = (i18n.language as Locale) || "it";

  // Strip locale prefix from path -> tail (e.g. /ru/prodotti/classic -> /prodotti/classic)
  const tail = pathname.replace(/^\/[a-z]{2}/, "") || "";
  const url = `${SITE_URL}/${currentLocale}${tail}`;
  const fullTitle = title.includes("La Contessina") ? title : `${title} — La Contessina`;
  const truncated = fullTitle.length > 70 ? fullTitle.slice(0, 67) + "…" : fullTitle;

  return (
    <Helmet>
      <html lang={currentLocale} dir={currentLocale === "ar" ? "rtl" : "ltr"} />
      <title>{truncated}</title>
      {description && <meta name="description" content={description.slice(0, 160)} />}
      <link rel="canonical" href={url} />

      {SUPPORTED_LOCALES.map((l) => (
        <link key={l} rel="alternate" hrefLang={l} href={`${SITE_URL}/${l}${tail}`} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/it${tail}`} />

      <meta property="og:type" content={type} />
      <meta property="og:title" content={truncated} />
      {description && <meta property="og:description" content={description.slice(0, 200)} />}
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={currentLocale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={truncated} />
      {description && <meta name="twitter:description" content={description.slice(0, 200)} />}
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd])}
        </script>
      )}
    </Helmet>
  );
}

/* ============================================================
 * JSON-LD payloads — Organization, LocalBusiness, WebSite,
 * Breadcrumb and ProductGroup helpers. Designed for AI search
 * (ChatGPT, Perplexity, Gemini, Copilot, AI Overviews).
 * ============================================================ */

const SAME_AS = [
  "https://www.instagram.com/lacontessina_srl/",
  "https://www.facebook.com/lacontessina",
  "https://www.youtube.com/@lacontessina",
  "https://www.linkedin.com/company/la-contessina",
  "https://flameplace.app/la-contessina",
];

const AVAILABLE_LANGUAGES = [
  "Italian", "English", "French", "Spanish", "Portuguese",
  "Russian", "Arabic", "Chinese", "Hindi",
];

/** Organization — global brand identity (cite-able by LLMs). */
export const ORGANIZATION_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "La Contessina",
  legalName: "La Contessina S.r.l.",
  alternateName: ["La Contessina Srl", "Contessina"],
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.ico`,
  image: DEFAULT_OG,
  description:
    "Italian family-owned manufacturer and General Interior Contractor since 1968. Classic and contemporary furniture, wardrobes, kitchens, boiserie, doors, parquet and interior textiles. 4,700+ partners in 40+ countries.",
  foundingDate: "1968",
  foundingLocation: {
    "@type": "Place",
    name: "Marano di Parma, Italy",
  },
  vatID: "IT02690380346",
  taxID: "IT02690380346",
  numberOfEmployees: { "@type": "QuantitativeValue", minValue: 30 },
  slogan: "General Interior Contractor since 1968",
  knowsAbout: [
    "Italian classic furniture",
    "Contemporary furniture",
    "Custom wardrobes",
    "Italian kitchens",
    "Boiserie wall panelling",
    "Interior doors",
    "Wood parquet",
    "Interior textiles",
    "Turnkey interior design",
    "General Interior Contracting",
  ],
  areaServed: { "@type": "AdministrativeArea", name: "Worldwide" },
  brand: { "@type": "Brand", name: "La Contessina" },
  hasCredential: [
    { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "ITPI — IT01 (100% Made in Italy)" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "ITPI — CI" },
  ],
  founder: [
    { "@type": "Person", name: "Famiglia Fagetti" },
  ],
  employee: [
    { "@type": "Person", name: "Gianluca Fagetti", jobTitle: "Managing Director" },
    { "@type": "Person", name: "Nicola Fagetti", jobTitle: "Managing Director" },
    { "@type": "Person", name: "Ivan Codjebas", jobTitle: "Sales & Marketing Director" },
  ],
  address: {
    "@type": "PostalAddress",
    streetAddress: "Via Marna, 18",
    addressLocality: "Marano di Parma",
    addressRegion: "PR",
    postalCode: "43040",
    addressCountry: "IT",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+39-0521-641060",
      email: "info@lacontessina.com",
      contactType: "sales",
      areaServed: "Worldwide",
      availableLanguage: AVAILABLE_LANGUAGES,
    },
    {
      "@type": "ContactPoint",
      telephone: "+39-348-8204372",
      email: "info@atelierelements.it",
      contactType: "showroom",
      areaServed: "IT",
      availableLanguage: ["Italian", "English"],
    },
  ],
  sameAs: SAME_AS,
};

/** WebSite — for sitelinks / brand recognition. */
export const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: "La Contessina",
  publisher: { "@id": `${SITE_URL}/#organization` },
  inLanguage: SUPPORTED_LOCALES,
};

/** Two LocalBusiness entries — one per showroom. */
export const LOCAL_BUSINESS_JSONLD = [
  {
    "@context": "https://schema.org",
    "@type": "FurnitureStore",
    "@id": `${SITE_URL}/#showroom-parma`,
    name: "La Contessina — Headquarters & Showroom Parma",
    url: `${SITE_URL}/it/showroom`,
    image: DEFAULT_OG,
    telephone: "+39-0521-641060",
    email: "info@lacontessina.com",
    priceRange: "€€€€",
    parentOrganization: { "@id": `${SITE_URL}/#organization` },
    address: {
      "@type": "PostalAddress",
      streetAddress: "Via Marna, 18",
      addressLocality: "Marano di Parma",
      addressRegion: "PR",
      postalCode: "43040",
      addressCountry: "IT",
    },
    geo: { "@type": "GeoCoordinates", latitude: 44.5485, longitude: 10.2310 },
    hasMap: "https://maps.app.goo.gl/3wzVXwHvvaQxQx3H9",
    areaServed: { "@type": "AdministrativeArea", name: "Worldwide" },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "09:00",
        closes: "13:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "14:00",
        closes: "18:00",
      },
    ],
    sameAs: SAME_AS,
  },
  {
    "@context": "https://schema.org",
    "@type": "FurnitureStore",
    "@id": `${SITE_URL}/#showroom-milano`,
    name: "Atelier Elements — La Contessina Showroom Milano (Brera)",
    url: `${SITE_URL}/it/showroom`,
    image: DEFAULT_OG,
    telephone: "+39-348-8204372",
    email: "info@atelierelements.it",
    priceRange: "€€€€",
    parentOrganization: { "@id": `${SITE_URL}/#organization` },
    address: {
      "@type": "PostalAddress",
      streetAddress: "Via San Simpliciano, 6",
      addressLocality: "Milano",
      addressRegion: "MI",
      postalCode: "20121",
      addressCountry: "IT",
    },
    geo: { "@type": "GeoCoordinates", latitude: 45.4720, longitude: 9.1860 },
    hasMap: "https://maps.app.goo.gl/sy4UXwacmxKZjqks7",
    areaServed: { "@type": "AdministrativeArea", name: "IT" },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        opens: "10:00",
        closes: "19:00",
      },
    ],
    sameAs: SAME_AS,
  },
];

/** Build a BreadcrumbList JSON-LD payload from {name, path} pairs. */
export function breadcrumbJsonLd(
  locale: Locale,
  items: { name: string; path: string }[],
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}/${locale}${it.path === "/" ? "" : it.path}`,
    })),
  };
}

/** Build a ProductGroup JSON-LD payload for a collection landing page. */
export function productGroupJsonLd(args: {
  name: string;
  description: string;
  url: string;
  image?: string;
  category?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "ProductGroup",
    name: args.name,
    description: args.description,
    url: args.url,
    image: args.image ?? DEFAULT_OG,
    category: args.category,
    brand: { "@id": `${SITE_URL}/#organization` },
    manufacturer: { "@id": `${SITE_URL}/#organization` },
  };
}